import { ApiError, NetworkError, isAborted } from '@/api/errors'
import { i18n, t } from './index'

/**
 * Turning a failure from the core into a sentence a French operator can act on.
 *
 * ## The rule: branch on `error.code`, never on `error.detail`
 *
 * The core answers failures as `application/problem+json` carrying the protocol's `error`
 * envelope. That envelope has two human-facing members and one machine-facing one:
 *
 * - `detail` / `error.message` — English prose, written for whoever is reading a server log. It is
 *   not a stable string. It gets reworded between releases, it interpolates values, and it is
 *   never translated.
 * - `error.code` — the machine-readable statement of **what the application decided**. It is an
 *   open enumeration on both sides of the protocol and it is the only member here that is safe to
 *   switch on.
 *
 * So the mapping is `code → key`. Matching on `detail` would produce an interface that silently
 * reverted to English the first time somebody improved a sentence in the core, and the failure
 * would be invisible in every test that used a fixture with the old wording.
 *
 * ## Unknown codes are rendered, never swallowed
 *
 * `ApiErrorCode` is deliberately open. A core newer than this build can answer with a code this
 * catalogue has never heard of, and the right behaviour is to say so *and show the code* — it is
 * what somebody pastes into a support thread. Falling back to a generic "something went wrong"
 * would throw away the one piece of information that identifies the problem.
 *
 * ## Why this lives in `i18n/` and not in `api/`
 *
 * The API client is an ordinary client of a public API and has no opinion about how a failure is
 * worded on screen. Keeping the catalogue lookup out of `api/errors.ts` is what lets that file be
 * tested — and reused — without a locale being installed.
 */

/** The English server message, kept for the console and for support threads. Never rendered. */
export const serverMessageOf = (error: unknown): string | null =>
  error instanceof ApiError ? error.message : null

/** Field-level violations from a `422`, as the core reported them. */
export const violationsOf = (error: unknown): Record<string, string> => {
  if (!(error instanceof ApiError)) return {}
  const violations = error.details.violations
  if (violations === null || typeof violations !== 'object' || Array.isArray(violations)) return {}

  return Object.fromEntries(
    Object.entries(violations as Record<string, unknown>).filter(
      (entry): entry is [string, string] => typeof entry[1] === 'string',
    ),
  )
}

const hasKey = (key: string): boolean => i18n.global.te(key)

/**
 * A translated sentence for any failure.
 *
 * `fallbackKey` names the surface that failed — `errors.loadMetrics`, `errors.loadEvents` — and is
 * used when the failure is not an `ApiError` at all (a bug in this client, a parse failure). It is
 * deliberately *not* used to override a recognised code: "the asset declares no such capability"
 * is more useful than "could not load metrics", and the specific message is always the better one.
 */
export const describeApiError = (error: unknown, fallbackKey = 'errors.unknown'): string => {
  if (isAborted(error)) return ''

  if (error instanceof NetworkError) return t('errors.network')

  if (error instanceof ApiError) {
    // A rate limit the core told us the length of is a different, more useful sentence.
    if (error.code === 'RATE_LIMITED' && error.retryAfter !== null) {
      return t('errors.RATE_LIMITED_RETRY', { seconds: error.retryAfter })
    }

    const key = `errors.${error.code}`
    if (hasKey(key)) return t(key)

    return t('errors.unrecognised', { code: error.code })
  }

  return hasKey(fallbackKey) ? t(fallbackKey) : t('errors.unknown')
}

/**
 * The three-way distinction a section needs before it renders an absence.
 *
 * `unsupported` and `unknown` are not the same fact and must not produce the same words:
 * "this asset cannot tell us" versus "we have not been told yet". `empty` is a complete answer —
 * we asked, and there is nothing.
 */
export type AbsenceReason = 'empty' | 'unsupported' | 'unknown' | 'error'

export const absenceReasonOf = (error: unknown): AbsenceReason => {
  if (!(error instanceof ApiError)) return 'error'
  if (error.isCapabilityMissing) return 'unsupported'
  if (error.isManifestUnavailable) return 'unknown'
  return 'error'
}
